import React from "react";
import { useNavigate } from "react-router-dom";

import { Icon } from "@iconify/react";
import { Button, Col, List, Row, Tag } from "antd";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";
import dayjs from "dayjs";

import { RentalRequestListProps } from "js/components/Properties/types";

const RentalRequestList: React.FC<RentalRequestListProps> = ({
  propertyRentalHistoryData,
  propertyId,
  onCreateRentalRequest,
  readOnly = false,
}) => {
  const navigate = useNavigate();
  const screen = useBreakpoint();

  const getStatusTag = (status: string): React.JSX.Element => {
    let color;
    let text = "";
    switch (status) {
      case "payment_in_progress":
      case "Payment in Progress":
        color = "cyan";
        text = "Pago Pendiente";
        break;
      case "completed":
      case "Completed":
        color = "green";
        text = "Completado";
        break;
      case "in_progress":
      case "In Progress":
        color = "orange";
        text = "En progreso";
        break;
      case "created":
        color = "blue";
        text = "Pendiente";
        break;
      case "Natural":
        color = "steelblue";
        text = "Persona Física";
        break;
      case "Legal":
        color = "lightseagreen";
        text = "Persona Moral";
        break;
      default:
        color = "gray";
        text = status;
        break;
    }
    return <Tag color={color}>{text}</Tag>;
  };

  return (
    <Row>
      <Col xs={24} sm={24} md={24} lg={24}>
        {propertyRentalHistoryData.length === 0 ? (
          // Section rendered when there aren't rental requests
          <Row
            style={{
              width: "100%",
              height: "250px",
              borderRadius: "8px",
              border: "1px solid rgba(140, 140, 140, 0.15)",
            }}
          >
            <Col
              xs={24}
              sm={24}
              md={24}
              lg={24}
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: 10,
              }}
            >
              <Icon color={"#8c8c8c"} fontSize={40} icon="bx:file" />
              <span
                style={{
                  fontSize: 16,
                  color: "#8c8c8c",
                  marginLeft: !screen.lg ? 10 : 0,
                }}
              >
                No existen registros de solicitudes de renta para este inmueble.
              </span>
              {!readOnly && (
                <Button type="primary" onClick={() => onCreateRentalRequest?.()}>
                  Crear Solicitud de Renta
                </Button>
              )}
            </Col>
          </Row>
        ) : (
          // Desktop and mobile view when there are rental requests
          <List
            size="small"
            style={{
              width: "100%",
              maxHeight: "250px",
              overflow: "auto",
              borderRadius: "8px",
              border: "1px solid rgba(140, 140, 140, 0.15)",
            }}
            dataSource={propertyRentalHistoryData}
            renderItem={(item) => {
              const formattedDate = dayjs(item.created_at).format("DD/MM/YYYY");
              return (
                <List.Item
                  key={item.id}
                  className="rental-history-list-item"
                  onClick={() => navigate(`/tenants/reports/${item.id}`)}
                  style={{
                    flexDirection: screen.xs ? "column" : "row",
                    alignItems: screen.xs ? "flex-start" : "center",
                    height: screen.xs ? "auto" : "60px",
                    padding: screen.xs ? "12px 8px" : undefined,
                  }}
                >
                  <Row style={{ width: "100%" }}>
                    <Col xs={24} sm={24} md={12} lg={4}>
                      <Tag>
                        <Row style={{ gap: 5 }}>
                          <span style={{ fontWeight: "bold" }}>Folio:</span>
                          <span style={{ fontWeight: "normal" }}>
                            {item.multiexpediente_folio}
                          </span>
                        </Row>
                      </Tag>
                    </Col>
                    <Col
                      style={{
                        display: "flex",
                        justifyContent: !screen.lg ? "start" : "center",
                        marginTop: screen.xs ? 10 : 0,
                      }}
                      xs={24}
                      sm={24}
                      md={12}
                      lg={6}
                    >
                      <Row style={{ gap: 5 }}>
                        <Icon fontSize={18} icon="uil:calendar" />
                        <span style={{ fontWeight: "normal" }}>
                          {formattedDate}
                        </span>
                      </Row>
                    </Col>
                    <Col
                      style={{
                        display: "flex",
                        justifyContent: !screen.lg ? "start" : "center",
                        marginTop: screen.xs ? 10 : 0,
                      }}
                      xs={24}
                      sm={24}
                      md={12}
                      lg={6}
                    >
                      <Row style={{ gap: 5 }}>
                        <Icon fontSize={18} icon="radix-icons:person" />
                        <span style={{ fontWeight: "normal" }}>
                          {item.person?.full_name || "Sin nombre"}
                        </span>
                      </Row>
                    </Col>
                    {screen.xs ? null : (
                      <Col
                        style={{
                          display: "flex",
                          justifyContent: "end",
                        }}
                        xs={24}
                        sm={24}
                        md={12}
                        lg={4}
                      >
                        {getStatusTag(item.legal_type)}
                      </Col>
                    )}
                    <Col
                      style={{
                        width: "100%",
                        position: screen.xs ? "absolute" : undefined,
                        display: "flex",
                        flexDirection: screen.xs ? "column" : undefined,
                        justifyContent: screen.xs ? "start" : "end",
                        alignItems: "end",
                        gap: screen.xs ? 10 : 0,
                      }}
                      xs={24}
                      sm={24}
                      md={12}
                      lg={4}
                    >
                      {getStatusTag(item.status)}
                      {screen.xs ? getStatusTag(item.legal_type) : null}
                    </Col>
                  </Row>
                </List.Item>
              );
            }}
          />
        )}
      </Col>
    </Row>
  );
};

export default RentalRequestList;
